import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { Target, Shield, MapPin, AlertTriangle, ExternalLink } from 'lucide-react';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useFacilitiesData } from '@/hooks/useFacilitiesData';
import { useSiteData } from '@/hooks/useSiteData';

export function Facilities() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();
  const { facilitiesData } = useFacilitiesData();

  const getFacilityIcon = (facilityName: string) => {
    const name = facilityName.toLowerCase();
    if (name.includes('indoor') || name.includes('outdoor') || name.includes('range')) {
      return Target;
    }
    return MapPin;
  };

  return (
    <>
      <Helmet>
        <title>Facilities - {siteData.title}</title>
        <meta name="description" content={facilitiesData.description} />
        <meta name="keywords" content="facilities, shooting range, indoor range, outdoor range, archery, firearms, Wayland Rod Gun Club" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              Facilities
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              {facilitiesData.description}
            </p>
          </div>

          <div className="space-y-8">
            {facilitiesData.facilities.map((facility, index) => {
              const IconComponent = getFacilityIcon(facility.name);
              return (
                <div key={index} className="card-professional hover:shadow-elegant transition-shadow">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg flex-shrink-0">
                      <IconComponent className="w-7 h-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl sm:text-3xl font-display font-semibold text-primary-700 mb-2">
                        {facility.name}
                      </h2>
                      <p className="text-foreground/80 leading-relaxed">
                        {facility.description}
                      </p>
                    </div>
                  </div>

                  {facility.specifications && (
                    <div className="card-professional bg-primary/5 mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Shield className="w-5 h-5 text-primary" />
                        <h3 className="text-xl font-display font-semibold text-primary-700">
                          Specifications
                        </h3>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {facility.specifications.positions && (
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                            <span className="text-foreground/80">
                              <span className="font-medium">Positions:</span> {facility.specifications.positions}
                            </span>
                          </div>
                        )}
                        {facility.specifications.length && (
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                            <span className="text-foreground/80">
                              <span className="font-medium">Length:</span> {facility.specifications.length}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-6">
                    {facility.features && (
                      <div>
                        <h3 className="text-xl font-display font-semibold text-primary-700 mb-4 flex items-center gap-2">
                          <Target className="w-5 h-5" />
                          Features
                        </h3>
                        <ul className="space-y-2">
                          {facility.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0"></span>
                              <span className="text-foreground/80 text-sm">
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {facility.rules && (
                      <div>
                        <h3 className="text-xl font-display font-semibold text-primary-700 mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5" />
                          Rules & Restrictions
                        </h3>
                        <ul className="space-y-2">
                          {facility.rules.map((rule, ruleIndex) => (
                            <li key={ruleIndex} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                              <span className="text-foreground/80 text-sm">
                                {rule}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {facility.caliberRestrictionsLink && (
                    <div className="mt-6 card-professional bg-amber-50 border-l-4 border-l-amber-500">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-amber-800">
                            For a complete list of calibers allowed, see:{' '}
                            <a
                              href={facility.caliberRestrictionsLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium underline decoration-2 underline-offset-2 transition-colors"
                            >
                              Indoor Range Firearm Caliber Restrictions
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Additional Information */}
          <div className="card-professional bg-muted/20">
            <div className="text-center">
              <h2 className="text-2xl font-display font-semibold text-secondary mb-4">
                Safety First
              </h2>
              <p className="text-foreground/80 max-w-3xl mx-auto leading-relaxed mb-6">
                All facilities operate under strict safety protocols. We offer Massachusetts State Police 
                certified Basic Firearms Safety courses and require all users to follow club safety rules.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/rules" 
                  className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-semibold transition-colors"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  View Safety Rules
                </a>
                <a 
                  href="/events" 
                  className="inline-flex items-center px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-lg font-semibold transition-colors"
                >
                  <Target className="w-4 h-4 mr-2" />
                  View Training Schedule
                </a>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}